import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Quote, X } from 'lucide-react';
import StarRating from '../ui/StarRating';
import SectionHeader from '../ui/SectionHeader';
import { api } from '../../lib/api';

function toTestimonial(review) {
  const memberSince = review.user?.createdAt
    ? new Date(review.user.createdAt).getFullYear()
    : null;

  return {
    id: review._id,
    name: review.user?.name || review.reviewerName || 'Anonymous',
    role: memberSince ? `Member since ${memberSince}` : 'Verified reader',
    text: review.text,
    rating: review.rating,
    avatar: review.user?.avatar,
  };
}

function TestimonialModal({ testimonial, onClose }) {
  return (
    <AnimatePresence>
      {testimonial && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 z-[90] mx-auto max-h-[80vh] max-w-lg -translate-y-1/2 overflow-y-auto rounded-xl2 bg-white dark:bg-surface-darkCard p-6 shadow-soft-lg sm:p-8"
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <Quote className="text-primary-200 dark:text-primary-900" size={28} />
              <button onClick={onClose} aria-label="Close" className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-white/10">
                <X size={18} />
              </button>
            </div>

            <p className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">{testimonial.text}</p>

            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-4 dark:border-white/10">
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-white">{testimonial.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{testimonial.role}</p>
              </div>
              <StarRating rating={testimonial.rating} showValue={false} className="ml-auto" size={13} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/books/testimonials/top')
      .then((data) => {
        if (!cancelled) setTestimonials((data.reviews || []).map(toTestimonial));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="container-app py-10">
      <SectionHeader chapter="11" title="What readers say" />

      {loading && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card-surface flex flex-col gap-4 p-6">
              <div className="h-4 w-3/4 skeleton rounded" />
              <div className="h-4 w-full skeleton rounded" />
              <div className="mt-auto flex items-center gap-3 pt-2">
                <div className="h-10 w-10 rounded-full skeleton" />
                <div className="h-4 w-24 skeleton rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && testimonials.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No reviews yet — be the first to share your thoughts on a book you've read.
        </p>
      )}

      {!loading && testimonials.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="card-surface flex flex-col gap-4 p-6"
            >
              <Quote className="text-primary-200 dark:text-primary-900" size={28} />
              <div>
                <p className="line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{t.text}</p>
                {t.text && t.text.length > 90 && (
                  <button
                    type="button"
                    onClick={() => setActive(t)}
                    className="mt-1 text-xs font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Read more
                  </button>
                )}
              </div>
              <div className="mt-auto flex items-center gap-3 pt-2">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">{t.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t.role}</p>
                </div>
                <StarRating rating={t.rating} showValue={false} className="ml-auto" size={13} />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <TestimonialModal testimonial={active} onClose={() => setActive(null)} />
    </section>
  );
}
